import { useCallback, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button } from '@material-ui/core';
import { isEmpty, merge } from 'lodash';

import AdminPage from '../AdminPage';
import { AdminPages, buildCreatePageTarget, buildPageId } from '../AdminLinkUtils';
import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE } from '../../data-table/SelectionContext';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { useQueryLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { DEFAULT_GROUP_UUID, GROUPS_ADMIN_LIST_COLUMNS } from './GroupsAdminConstants';
import { Actions } from '../../page-framework';
import Link from '../../links/Link';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { useIsSysAdminUser } from '../../../stores/SessionStore';

const functionName = AdminPages.Groups;

const initialSort = [{ property: 'groupName', direction: SORT_ASC }];
const dataSource = {
    url: '/ipm/v1/admin/groups',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['groupName', 'description'],
    },
};

export default function GroupsAdminList() {
    const [actionsDisabled, setActionsDisabled] = useState(true);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [tableState, setTableState] = useState(undefined);
    const loader = useQueryLoader(dataSource, { disableFilterState: true });
    const formDeleteService = useFormDeleteService();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const { deleteWrapper } = useFormDeleteWrapper();
    const [isSysAdmin] = useIsSysAdminUser();

    const handleTableSelectionChange = useCallback((currentTableSelection) => {
        const disableActions =
            !currentTableSelection || (isEmpty(currentTableSelection?.selection) && !currentTableSelection.inverted);

        setTableSelection(currentTableSelection);
        setActionsDisabled(disableActions);
    }, []);

    const handleConfirmedDelete = () => {
        const params = merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(`${dataSource.url}/selection/remove`, params, tableSelection),
            refresherFunc: refresher,
        })();
    };

    return (
        <AdminPage
            title={t`Groups`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {isSysAdmin && (
                        <ConfirmDeleteButton
                            disabled={actionsDisabled}
                            handleConfirmedDelete={handleConfirmedDelete}
                            plural={tableSelection?.selection.length > 1}
                        />
                    )}
                    {isSysAdmin && (
                        <Button
                            component={Link}
                            to={buildCreatePageTarget(functionName)}
                            variant="contained"
                            color="primary"
                        >
                            <Trans>Create</Trans>
                        </Button>
                    )}
                </Actions>
            }
        >
            <DataTable
                tableId="admin-groups-list"
                loader={loader}
                columns={GROUPS_ADMIN_LIST_COLUMNS}
                triggerRef={triggerRef}
                visibleRows={0}
                showSelectionColumn={isSysAdmin}
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionColumnRenderCondition={(row) => row._id !== DEFAULT_GROUP_UUID}
                selectionOptions={{
                    mode: isSysAdmin ? SELECTIONMODE_MULTI : SELECTIONMODE_NONE,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
